import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { entityTypeApi } from "../../api/entityTypeApi";
import type { EntityType } from "../../types/entityType.types";
import type { RootState, AppDispatch } from "../../app/store";
import { toast } from "../../components/common/toast";
import { getApiErrorMessage } from "../../utils/apiError";
import { reportDeleteFailure } from "../../utils/deleteFailure";
import i18n from "../../i18n";
import { getCurrentUser } from "../../auth/auth.service";

interface EntityTypeState {
  items: EntityType[];
  isLoading: boolean;
  isAdding: boolean;
  isEditing: boolean;
  isDeleting: boolean;
  hasMore: boolean;
  pageIndex: number;
  totalCount: number;
  error: string | null;
}

const initialState: EntityTypeState = {
  items: [],
  isLoading: false,
  isAdding: false,
  isEditing: false,
  isDeleting: false,
  hasMore: true,
  pageIndex: 0,
  totalCount: 0,
  error: null,
};

const entityTypeSlice = createSlice({
  name: "entityTypes",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setAdding: (state, action: PayloadAction<boolean>) => {
      state.isAdding = action.payload;
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setDeleting: (state, action: PayloadAction<boolean>) => {
      state.isDeleting = action.payload;
    },
    setEntityTypes: (state, action: PayloadAction<EntityType[]>) => {
      state.items = action.payload;
    },
    appendEntityTypes: (state, action: PayloadAction<EntityType[]>) => {
      state.items = [...state.items, ...action.payload];
    },
    addSingleEntityTypeLocally: (state, action: PayloadAction<EntityType>) => {
      state.items.unshift(action.payload);
      state.totalCount += 1;
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    setPageIndex: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },
    resetEntityTypeList: (state) => {
      state.items = [];
      state.hasMore = true;
      state.pageIndex = 0;
      state.totalCount = 0;
    },
    updateSingleEntityType: (
      state,
      action: PayloadAction<Partial<EntityType> & { id: string }>,
    ) => {
      const index = state.items.findIndex((et) => et.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...action.payload };
      }
    },
    removeSingleEntityType: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((et) => et.id !== action.payload);
      state.totalCount = Math.max(0, state.totalCount - 1);
    },
  },
});

export const {
  setLoading,
  setAdding,
  setEditing,
  setDeleting,
  setEntityTypes,
  appendEntityTypes,
  addSingleEntityTypeLocally,
  setTotalCount,
  setHasMore,
  setPageIndex,
  resetEntityTypeList,
  updateSingleEntityType,
  removeSingleEntityType,
} = entityTypeSlice.actions;

interface FetchEntityTypesPayload {
  pageIndex?: number;
  pageSize?: number;
  orderBy?: string;
  sortAscending?: boolean;
  /** Pickers pass true so deactivated entity types are never offered. */
  isActive?: boolean | null;
  groupIds?: string[];
  ids?: string[];
  searchText?: string;
  includeDeleted?: boolean;
}

export const fetchEntityTypes = (payload: FetchEntityTypesPayload = {}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().entityTypes;
    const userData = await getCurrentUser();

    const incomingPageIndex = payload.pageIndex ?? state.pageIndex;

    if (state.isLoading || (!state.hasMore && incomingPageIndex !== 0)) return;

    try {
      dispatch(setLoading(true));

      const requestBody = {
        pageSize: payload.pageSize || 10,
        pageIndex: incomingPageIndex,
        orderBy: payload.orderBy || "string",
        // This endpoint reads the flag as "ascending" despite its name — a
        // quirk every dropdown in the app relies on, so it is matched here
        // rather than changed there.
        sortDecending: payload.sortAscending ?? false,
        groupIds: payload.groupIds || [userData?.companyId],
        ids: payload.ids || [],
        searchText: payload.searchText || "",
        includeDeleted: payload.includeDeleted || false,
        ...(payload.isActive !== undefined && { isActive: payload.isActive }),
      };

      const response = await entityTypeApi.searchEntityTypes(requestBody);
      const fetchedData = response.data || [];
      const totalCount = response.count || response.totalCount || 0;

      dispatch(setTotalCount(totalCount));

      const currentLoadedCount =
        incomingPageIndex === 0
          ? fetchedData.length
          : state.items.length + fetchedData.length;

      if (currentLoadedCount >= totalCount || fetchedData.length < 10) {
        dispatch(setHasMore(false));
      } else {
        dispatch(setHasMore(true));
      }

      if (incomingPageIndex === 0) {
        dispatch(setEntityTypes(fetchedData));
        dispatch(setPageIndex(1));
      } else {
        dispatch(appendEntityTypes(fetchedData));
        dispatch(setPageIndex(incomingPageIndex + 1));
      }
    } catch (error) {
      console.error("Fetch Entity Types error:", error);
      toast.error(
        i18n.t("entityTypes:entityType.messages.fetchError", {
          defaultValue: "Failed to fetch entity types",
        }),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const addEntityType = (data: { entityName: string }) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setAdding(true));

      const payload = {
        entityName: data.entityName,
      };

      const res = await entityTypeApi.createEntityType(payload);

      const createdId = res?.data?.id || res?.data || res?.id || res;

      const newEntityType: EntityType = {
        entityName: data.entityName,
        id: typeof createdId === "string" ? createdId : createdId?.id,
        groupId: res?.data?.groupId || res?.groupId || "",
      };

      dispatch(addSingleEntityTypeLocally(newEntityType));

      toast.success(
        i18n.t("entityTypes:entityType.messages.addSuccess", {
          defaultValue: "Entity Type created successfully",
        }),
      );
      return newEntityType;
    } catch (error) {
      console.error("Create Entity Type error:", error);
      toast.error(getApiErrorMessage(error, i18n.t("entityTypes:entityType.messages.addError", {
          defaultValue: "Failed to create entity type",
        })));
      throw error;
    } finally {
      dispatch(setAdding(false));
    }
  };
};

export const editEntityType = (data: { id: string; entityName: string }) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setEditing(true));

      const payload = {
        id: data.id,
        entityName: data.entityName,
      };

      const res = await entityTypeApi.updateEntityType(payload);
      const updatedEntityType = res?.data || payload;

      dispatch(updateSingleEntityType(updatedEntityType));

      toast.success(
        i18n.t("entityTypes:entityType.messages.editSuccess", {
          defaultValue: "Entity Type updated successfully",
        }),
      );
      return updatedEntityType;
    } catch (error) {
      console.error("Edit Entity Type error:", error);
      toast.error(getApiErrorMessage(error, i18n.t("entityTypes:entityType.messages.editError", {
          defaultValue: "Failed to update entity type",
        })));
      throw error;
    } finally {
      dispatch(setEditing(false));
    }
  };
};

export const removeEntityType = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setDeleting(true));

      dispatch(removeSingleEntityType(id));

      await entityTypeApi.removeEntityType(id);

      toast.success(
        i18n.t("entityTypes:entityType.messages.removeSuccess", {
          defaultValue: "Entity Type removed successfully",
        }),
      );
    } catch (error) {
      console.error("Remove Entity Type error:", error);
      // The API refuses deletion while contracts or staff still use the
      // record and says which — show that reason, not a generic failure.
      const fallback = i18n.t("entityTypes:entityType.messages.removeError", {
        defaultValue: "Failed to remove entity type",
      });
      reportDeleteFailure(error, fallback);
      throw error;
    } finally {
      dispatch(setDeleting(false));
    }
  };
};

export default entityTypeSlice.reducer;

export const activateEntityType = (id: string) => {
  return async (dispatch: AppDispatch) => {
    // Optimistic, then rolled back on failure — same shape as the
    // department/business-unit toggles.
    dispatch(updateSingleEntityType({ id, isActive: true } as any));

    try {
      await entityTypeApi.activateEntityType(id);

      toast.success(
        i18n.t("entityTypes:entityType.messages.activateSuccess", {
          defaultValue: "Entity Type activated",
        }),
      );
    } catch (error) {
      dispatch(updateSingleEntityType({ id, isActive: false } as any));
      console.error("Activate Entity Type error:", error);
      toast.error(
        i18n.t("entityTypes:entityType.messages.activateError", {
          defaultValue: "Failed to activate entity type",
        }),
      );
    }
  };
};

export const deactivateEntityType = (id: string) => {
  return async (dispatch: AppDispatch) => {
    dispatch(updateSingleEntityType({ id, isActive: false } as any));

    try {
      await entityTypeApi.deactivateEntityType(id);

      toast.success(
        i18n.t("entityTypes:entityType.messages.deactivateSuccess", {
          defaultValue: "Entity Type deactivated",
        }),
      );
    } catch (error) {
      dispatch(updateSingleEntityType({ id, isActive: true } as any));
      console.error("Deactivate Entity Type error:", error);
      toast.error(
        i18n.t("entityTypes:entityType.messages.deactivateError", {
          defaultValue: "Failed to deactivate entity type",
        }),
      );
    }
  };
};
